<template>
  <div class="files">
    <dropzone
      v-if="this.$hasUserPermission('PER001')"
      class="mt-3"
      :input-accept="allowedFiles"
      :title="$t('asset-information.asset-info.files.attach-files')"
      @input="onAddFile"
    />
    <div class="mt-3">
      <label
        v-if="this.$hasUserPermission('PER001')"
        class="files__table-title" >
        {{ $t('asset-information.menu.files') }}
      </label>
    <s-loader
      v-if="loading"
      class="mt-2"
      size="lg"
    />
      <s-table
        v-else
        class="files__table"
        :fields="fields"
        :items="files"
      >
        <template #cell(name)="{ item }">
          <div>
            {{ item.name }}
          </div>
          <div v-show="item.percentageSent >= 0" class="text-primary">
            <em>{{ $t('general.loading') + '(' + item.percentageSent + '%)' }}</em>
          </div>
        </template>
        <template #cell(uploadDateTime)="{ item }">
          <span class="files__text-gray">
            {{
              item.uploadDateTime | dateTimeFilter({
                format: formats.DATETIME_SHORT_WITH_SECONDS
              })
            }}
          </span>
        </template>
        <template #cell(size)="{ item }">
          <span class="files__text-gray">
            {{ convertBytes(item.size) }}
          </span>
        </template>
        <template
          v-if="this.$hasUserPermission('PER001')"
          #cell(actions)="{ item }">
          <b-button
            v-if="!isFileProcessing(item)"
            variant="outline-green"
            size="sm"
            @click="downloadFile(item)"
          >
            <i
              class="files__icon fas fa-download"
              aria-hidden="true"
            />
          </b-button>
          <b-button
            variant="outline-danger"
            size="sm"
            @click="deleteFile(item)"
          >
            <i
              class="files__icon fas fa-trash-alt"
              aria-hidden="true"
            />
          </b-button>
        </template>
      </s-table>
    </div>
    <div class="text-right">
      <span class="files__text-gray files__remaining-size">
        {{ $t('asset-information.asset-info.files.asset-remaining-size') }}:
      </span>
      <span class="text-primary">
        {{ convertBytes(details.remainingSpaceAvailable) }}
      </span>
    </div>
  </div>
</template>

<script>
import loadingMixin from '@/services/loadingMixin';
import Dropzone from '@/components/Dropzone';
import filesTableFields from '@/domain/data/installation/assetInformation/filesTableFields';
import { formats } from '@/constants/localizer';
import FilesService from '@/domain/services/installation/assetInfo/files';
import TreeviewHeaderSubtitleMixin from '@/components/treeview/treeviewHeaderSubtitleMixin';

export default {
  mixins: [
    loadingMixin,
    TreeviewHeaderSubtitleMixin,
  ],
  components: {
    Dropzone,
  },
  data: () => ({
    formats,
    allowedFiles: [],
    details: {},
    files: [],
    uploadManager: {},
    hasUploadError: false,
  }),
  created() {
    this.init();
  },
  computed: {
    fields() {
      return filesTableFields(this);
    },
    assetId() {
      const { assetId } = this.$route.params;

      return assetId;
    },
  },
  methods: {
    init() {
      this.getFilesList();
      this.getDetails();
      this.updateHeaderTitle(this.$t('asset-information.menu.files'));
    },
    getFilesList() {
      this.startLoading();

      return FilesService
        .getFiles(this.assetId)
        .then((data) => {
          this.files = [];
          this.files = data;
        }).finally(() => {
          this.endLoading();
        });
    },
    getDetails() {
      FilesService
        .getFilesDetails(this.assetId)
        .then((data) => {
          this.details = data;
          this.details.maxFileSize = this.convertBytes(data.maxFileSize, true);
          this.details.maxAssetSize = this.convertBytes(data.maxAssetSize, true);

          this.allowedFiles = data.allowedExtensions;

          this.uploadManager = this.createUploadManager();
        });
    },
    isFileProcessing(file) {
      return file.percentageSent >= 0;
    },
    onAddFile(addedFiles) {
      if (this.files.length === 0) {
        this.hasUploadError = false;
      }

      this.files = [
        ...this.files,
        ...addedFiles.map(file => ({
          file,
          id: null,
          percentageSent: 0,
          name: file.name,
        })),
      ];
      this.uploadManager.appendFile(...addedFiles);
    },
    deleteFile(file) {
      this.$confirmMessage({ code: 'MC028' })
        .accepted(() => this.confirmDelete(file));
    },
    confirmDelete(file) {
      if (this.isFileProcessing(file)) {
        this.abortUpload(file);
        return;
      }

      FilesService
        .deleteFile(this.assetId, file.id)
        .then(() => {
          this.$pushAlert(
            {
              code: 'MS012',
              autoDismissible: true,
            },
          );
          this.init();
        });
    },
    downloadFile(file) {
      FilesService.downloadFile(
        this.assetId,
        file.id,
      );
    },
    createUploadManager() {
      return FilesService.createUploadManager(
        this.assetId,
        this.details,
        {
          listeners: {
            'retrieved-file-id': this.onRetrievedFileId,
            'on-progress': this.onUploadProgress,
            'on-success': this.onUploadSuccess,
            'on-error': this.onUploadError,
          },
          listenersScope: this,
        },
      );
    },
    onRetrievedFileId({ file, id }) {
      const fileData = this.getFileData(file);
      this.$set(fileData, 'id', id);
    },
    onUploadProgress({ file, percentageSent }) {
      const fileData = this.getFileData(file);

      if (fileData) {
        this.$set(fileData, 'percentageSent', Math.round(percentageSent));
      }
    },
    onUploadSuccess() {
      if (!this.hasUploadError) {
          this.$pushAlert(
            {
              code: 'MS029',
              autoDismissible: true,
            },
          );
        this.init();
      }
    },
    onUploadError(file) {
      this.removeFileAndRefreshListIfNeeded(file);
      this.hasUploadError = true;
    },
    async removeFileAndRefreshListIfNeeded(file) {
      const fileData = this.getFileData(file);

      this.removeFileData(fileData);
    },
    abortUpload(item) {
      this.uploadManager.stop(item.file);
    },
    getFileData(file) {
      return this.files.find(fileData => fileData.file === file);
    },
    removeFileData(fileData) {
      this.files.splice(this.files.indexOf(fileData), 1);
    },
    getFileDataById(fileId) {
      return this.files.find(({ id }) => id === fileId);
    },
    convertBytes(bytes, withSufixes = true) {
      if (bytes === null || typeof bytes !== 'number') {
        return '';
      }

      const sufixes = ['B', 'kB', 'MB', 'GB'];

      let order = 0;
      let result = bytes;

      while (result >= 1024 && order < sufixes.length - 1) {
        result /= 1024;
        order += 1;
      }

      if (withSufixes) {
        return `${result.toFixed(2)} ${sufixes[order]}`;
      }

      return `${result.toFixed(2)}`;
    },
  },
};
</script>

<style lang="sass" scoped>
  .files
    &__icon
      width: 14px
      height: 14px
      font-size: 13px
    &__table-title
      font-size: 1.2rem
    &__text-gray
      color: var(--font-color-secondary)
    &__table
      ::v-deep
        .files
          &__table-actions
            width: 115px
        th:first-child
          text-align: center !important
</style>
